import {useTranslation} from '@aboutyou/i18n';
import React, {useEffect, useState, type JSX} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {Button} from '../Buttons/Button';
import {Icon} from '../Icon/Icon';
import {Text} from '../Text/Text';
import {ErrorBoundaryIconName} from './assets';
// eslint-disable-next-line no-restricted-imports, import/no-extraneous-dependencies
import {useQueryClient} from '@aboutyou/data-fetcher';
import {useCanGoBack, useHistory} from '@aboutyou/router';

interface ErrorPageProps {
  RowComp?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  headline?: string;
  subline?: string | false;
  /**
   * If a URL is provided, the Continue shopping button will navigate to that URL.
   * If no URL is provided, the "Continue shopping" button will navigate back in history if possible, otherwise it will navigate to the homepage.
   * If the provided URL is the same as the current page, the page will be hard reloaded.
   */
  url?: string;
  resetError?: (options: {isRetrying: boolean}) => void;
  retried?: boolean;
}

export const ErrorPageInline: React.FC<
  React.PropsWithChildren<ErrorPageProps>
> = React.memo(function ErrorPageInline({
  headline: headlineProp,
  RowComp,
  subline,
  url,
  resetError,
  retried,
}) {
  const {t} = useTranslation();
  const defaultHeadline = t('Oops! Something went wrong!');
  const headline = headlineProp || defaultHeadline;

  const queryClient = useQueryClient();
  const [retrying, setRetrying] = useState(false);

  const history = useHistory();
  const canGoBack = useCanGoBack();

  // Reset error state on route change
  useEffect(() => {
    return history.listen(
      () => {
        resetError?.({isRetrying: false});
      },
      {disableUiUpdates: true},
    );
  }, [history, resetError]);

  return (
    <>
      <Container>
        <RowContainer as={RowComp}>
          <IconContainer>
            <Icon
              asset={ErrorBoundaryIconName.FlashWarning}
              size="XL"
              fill={COLORS['Alerts.Error.Primary']}
            />
          </IconContainer>
          <MessageContainer>
            <Text color="Black" size="px18" weight="bold">
              {headline}
            </Text>
            {subline && (
              <Text color="Grey1" size="px14">
                {subline}
              </Text>
            )}
          </MessageContainer>
        </RowContainer>
      </Container>

      <ButtonContainer>
        <Button
          variant="primary"
          size="lg"
          onClick={() => {
            queryClient.invalidateQueries();
            setRetrying(true);

            setTimeout(() => {
              resetError?.({isRetrying: true});
            }, 1000);
          }}
          disabled={retried}
          isLoading={retrying}
        >
          {t('Try again')}
        </Button>

        <StyledButton
          href={url}
          variant="primary"
          size="lg"
          onClick={() => {
            if (url === window.location.pathname) {
              window.location.reload();
              return;
            }

            if (canGoBack) {
              history.goBack();
              return;
            }

            if (!url) {
              history.push('/');
            }
          }}
        >
          {t('Continue shopping')}
        </StyledButton>
      </ButtonContainer>
    </>
  );
});

const Container = styled.div`
  padding: 30px 20px;
  background-color: ${COLORS['Alerts.Error.Secondary']};
`;

const IconContainer = styled.div`
  margin-right: 20px;
`;

const RowContainer = styled.div`
  justify-content: center;
  display: flex;
  flex: 1;
  align-items: center;
  height: 60px;
`;

const ButtonContainer = styled.div`
  justify-content: center;
  display: flex;
  align-items: center;
  margin-top: 30px;
  gap: 1rem;
`;

const StyledButton = styled(Button)`
  height: 48px;
`;

const MessageContainer = styled.div`
  height: 100%;
  justify-content: space-between;
  display: flex;
  flex-direction: column;
`;
